import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from './LanguageSwitcher';
import styles from '../../styles/components/LegalFooter.module.css';

/**
 * LegalFooter Component
 * Rechtlich erforderliche Links - von jeder Seite aus erreichbar
 *
 * Varianten:
 * - 'minimal': Nur Links in einer Zeile (für App-Bereich)
 * - 'compact': Links mit Trennzeichen (für öffentliche Seiten)
 * - 'full': Mit Copyright und allen Infos (für Landing-ähnliche Seiten)
 *
 * @param {string} variant - 'minimal' | 'compact' | 'full'
 * @param {string} className - Zusätzliche CSS-Klasse
 */
function LegalFooter({ variant = 'minimal', className = '', creatorUsername = null }) {
  const { t } = useTranslation();

  const legalLinks = [
    { to: '/impressum', label: t('legalFooter.impressum') },
    { to: '/datenschutz', label: t('legalFooter.datenschutz') },
    { to: '/agb', label: t('legalFooter.agb') },
    { to: '/widerruf', label: t('legalFooter.widerruf') },
  ];

  // Minimal: Nur für eingeloggten Bereich
  if (variant === 'minimal') {
    return (
      <footer className={`${styles.legalFooter} ${styles.minimal} ${className}`}>
        <nav className={styles.links}>
          {legalLinks.map((link, index) => (
            <React.Fragment key={link.to}>
              <Link to={link.to} className={styles.link}>
                {link.label}
              </Link>
              {index < legalLinks.length - 1 && (
                <span className={styles.separator}>·</span>
              )}
            </React.Fragment>
          ))}
        </nav>
        <LanguageSwitcher />
      </footer>
    );
  }

  // Compact: Für öffentliche Seiten (PublicStore, PublicProduct)
  if (variant === 'compact') {
    const registerUrl = creatorUsername
      ? `/register?ref=store-footer&creator=${creatorUsername}`
      : '/register?ref=store-footer';

    return (
      <footer className={`${styles.legalFooter} ${styles.compact} ${className}`}>
        <div className={styles.compactContent}>
          <div className={styles.brandSection}>
            <span className={styles.poweredBy}>
              {t('legalFooter.poweredBy')}{' '}
              <Link to="/" className={styles.brandLink}>MoneMee</Link>
            </span>
            <span className={styles.separator}>·</span>
            <Link to={registerUrl} className={styles.ctaLink}>
              {t('legalFooter.cta')}
            </Link>
          </div>
          <nav className={styles.links}>
            {legalLinks.map((link, index) => (
              <React.Fragment key={link.to}>
                <Link to={link.to} className={styles.link}>{link.label}</Link>
                {index < legalLinks.length - 1 && (
                  <span className={styles.separator}>·</span>
                )}
              </React.Fragment>
            ))}
          </nav>
          <LanguageSwitcher />
        </div>
      </footer>
    );
  }

  // Full: Für Landing-ähnliche Seiten mit Copyright
  return (
    <footer className={`${styles.legalFooter} ${styles.full} ${className}`}>
      <div className={styles.fullContent}>
        <nav className={styles.links}>
          {legalLinks.map((link) => (
            <Link key={link.to} to={link.to} className={styles.link}>
              {link.label}
            </Link>
          ))}
        </nav>
        <LanguageSwitcher />
        <p className={styles.copyright}>
          {t('legalFooter.copyright', { year: new Date().getFullYear() })}
        </p>
      </div>
    </footer>
  );
}

export default LegalFooter;
